<template>
  <div class="content-container">
    <!-- If voting is closed -->
    <div v-if="isExpired">
      <div class="mdl-card__title expired">
        <i class="material-icons warning">warning</i>
        <h2 class="mdl-card__title-text">Submissions are closed</h2>
      </div>
      <div class="mdl-card__supporting-text">
        <p>
          {{ submissionsClosedDescText }}
        </p>
      </div>
    </div>

    <!-- Otherwise voting is still open -->

    <!-- If only verified voting is allowed AND this voter is verified -->
    <div v-else-if="blockUnverifiedVoting && !isVerified">
      <div class="mdl-card__title expired">
        <i class="material-icons warning">warning</i>
        <h2 class="mdl-card__title-text">
          You are not authorized to vote online
        </h2>
      </div>
      <div class="mdl-card__supporting-text">
        <p>
          You must click on the verification link in the notice email. Please
          contact management if you require the notice email to be resent.
        </p>
      </div>
    </div>

    <!-- If blocked recasting is true and owner has previously submitted vote -->
    <div v-else-if="isBlockedRecast">
      <div class="mdl-card__title expired">
        <i class="material-icons warning">warning</i>
        <h2 class="mdl-card__title-text">Vote re-casting has been disabled</h2>
      </div>
      <div class="mdl-card__supporting-text">
        <p>
          Our records indicate that you previously voted on
          {{ formatLastVotedAt }}. Please contact voters@getquorum.com if you
          believe this to be incorrect.
        </p>
      </div>
    </div>

    <!-- If blocked recasting is true and unit has previously submitted vote -->
    <div v-else-if="isBlockedUnitRecast">
      <div class="mdl-card__title expired">
        <i class="material-icons warning">warning</i>
        <h2 class="mdl-card__title-text">
          You have casted your vote
        </h2>
      </div>
      <div class="mdl-card__supporting-text">
        <p>
          Our records indicate that your vote was received on
          {{ formatUnitLastVotedAt }}. Please contact voters@getquorum.com if
          you believe this to be incorrect.
        </p>
      </div>
    </div>

    <!-- Voter can Vote -->
    <div v-else>
      <!-- Simple mode will hide this text -->
      <div v-if="mode !== 'simple'">
        <div class="mdl-card__title aligner">
          <h5 class="mdl-card__title-text">{{ startProxyText }}</h5>
        </div>
        <div class="mdl-card__supporting-text aligner">
          <p>It's quick and easy.</p>
        </div>
      </div>

      <div class="action-bar">
        <button-component
          color="primary"
          :disabled="isVotingDisabled || noVoterMultiConsent"
          value="Let's Get Started"
          log-action="start-proxy"
        >
          <span slot="icon"><i class="material-icons">send</i></span>
        </button-component>
      </div>

      <p v-if="isVotingDisabled || noVoterMultiConsent" class="aligner">
        <b><i>You are not eligible to vote.</i></b>
      </p>

      <p v-if="ineligibleReason === 'arrears'" class="aligner">
        <b>
          <i>
            Your unit is in arrears. Please contact management for more
            information.
          </i>
        </b>
      </p>

      <p v-if="ineligibleReason === 'paper-proxy'" class="aligner">
        <b>
          <i>
            You have submitted a paper proxy. Please contact management for more
            information.
          </i>
        </b>
      </p>

      <p v-if="noVoterMultiConsent" class="aligner">
        <b>
          <i>
            You have not given your consent to electronic voting, or you chose
            to decline it.
          </i>
        </b>
      </p>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import {mapGetters} from 'vuex';
import {formatDate} from '@/vuex/helpers.js';
import ButtonComponent from '../Button.vue';

export default {
  props: {
    isClosed: Boolean,
    isExpired: Boolean,
    shortCode: String,
    view: String,
    mode: {
      type: String,
      default: ''
    },
    displayOptions: {
      type: Object,
      default() {
        return {};
      }
    },
    meetingTimezone: {
      type: String,
      default: null
    },
    isBlockedRecast: {
      type: Boolean,
      default: false
    },
    isBlockedUnitRecast: {
      type: Boolean,
      default: false
    },
    lastVotedAt: {
      type: String,
      default: null
    },
    unitLastVotedAt: {
      type: String,
      default: null
    }
  },
  components: {
    ButtonComponent
  },
  computed: {
    ...mapGetters({
      blockUnverifiedVoting: 'blockUnverifiedVoting'
    }),
    ...mapGetters('voter', [
      'voter',
      'isVerified',
      'isVotingDisabled',
      'noVoterMultiConsent'
    ]),
    formatLastVotedAt() {
      return this.formatVotedAt(this.lastVotedAt);
    },
    formatUnitLastVotedAt() {
      return this.formatVotedAt(this.unitLastVotedAt);
    },
    timezone() {
      return this.meetingTimezone ? this.meetingTimezone : 'America/Toronto';
    },
    voterCanVote() {
      return this.isVerified;
    },
    showContent() {
      if (!this.isExpired && this.isClosed) {
        return true;
      } else if (!this.isClosed) {
        return true;
      } else {
        return false;
      }
    },
    submissionsClosedDescText() {
      return _.has(
        this.displayOptions,
        'home.customize_submissions_closed_desc_text'
      )
        ? this.displayOptions.home.customize_submissions_closed_desc_text
        : 'Please attend the event to cast your vote.';
    },
    startProxyText() {
      return _.has(this.displayOptions, 'home.customize_start_proxy_text')
        ? this.displayOptions.home.customize_start_proxy_text
        : 'Cast Your Vote Online Now!';
    },
    ineligibleReason() {
      if (
        this.voter.customUnitData &&
        'ineligible_reason' in this.voter.customUnitData &&
        this.voter.customUnitData.ineligible_reason !== ''
      ) {
        return this.voter.customUnitData.ineligible_reason;
      } else {
        return '';
      }
    }
  },
  methods: {
    formatVotedAt(event) {
      return formatDate(event, this.timezone, 'dddd, MMMM Do, YYYY h:mm a zz');
    }
  }
};
</script>

<style scoped>
/* Title Text */
.mdl-card__title-text {
  font-weight: 500;
  font-size: 18px;
  color: #424242;
}
/* Action bar */
.action-bar {
  display: flex;
  justify-content: center;
  width: 100%;
  padding-bottom: 10px;
}
.action-bar > .mdl-button {
  height: 40px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Spacing above supporting text */
.mdl-card__supporting-text {
  padding-top: 0px;
}

/* Expired container */
.mdl-card__title > .material-icons {
  padding-left: 3px;
  padding-right: 17px;
  font-size: 34px;

  /* warning icon */
  color: #bdbdbd;
}

@media only screen and (max-width: 325px) {
  .mdl-card__title.expired > .mdl-card__title-text {
    font-size: 14px;
  }
}
@media only screen and (min-width: 767px) {
  .content-container {
    padding: 0px 15px 10px;
  }
}
</style>
